import { PagingParameters } from "@/api/dtos";
import { ReactNode, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import { Input } from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export interface DatatablePaginationProps {
  pagination: PagingParameters;
  setPage: React.Dispatch<SetStateAction<PagingParameters>>;
  totalSize: number;
  /** Actions affichées à droite de « Aller à la page » (colonnes, vue liste/grille, etc.). */
  endContent?: ReactNode;
}

interface InnerPaginatorProps {
  lastPage: number;
  props: DatatablePaginationProps;
}

const PaginationElement = (props: {
  num: number;
  pagination: PagingParameters;
  setPagination: React.Dispatch<SetStateAction<PagingParameters>>;
}) => {
  return (
    <button
      type="button"
      className={
        "select-none cursor-pointer align-middle size-10 rounded-lg flex items-center justify-center" +
        (props.num === props.pagination.page
          ? " border-solid border-[1px] border-slate-300"
          : "")
      }
      onClick={() =>
        props.setPagination({
          page: props.num,
          pageSize: props.pagination.pageSize,
        })
      }
    >
      {props.num + 1}
    </button>
  );
};

const PaginationElipsis = () => {
  return (
    <div className="select-none text-center align-middle size-10 rounded-lg flex items-center justify-center text-lg">
      ...
    </div>
  );
};

const FewPagesPaginator = ({ lastPage, props }: InnerPaginatorProps) => {
  return new Array(lastPage + 1)
    .fill(0)
    .map((_, i) => (
      <PaginationElement
        key={i}
        num={i}
        pagination={props.pagination}
        setPagination={props.setPage}
      />
    ));
};

const ExtremitiesPaginator = ({ lastPage, props }: InnerPaginatorProps) => {
  return (
    <>
      <PaginationElement
        num={0}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={1}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={2}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElipsis />
      <PaginationElement
        num={lastPage - 2}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={lastPage - 1}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={lastPage}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
    </>
  );
};

const MiddlePaginator = ({ lastPage, props }: InnerPaginatorProps) => {
  return (
    <>
      <PaginationElement
        num={0}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElipsis />
      <PaginationElement
        num={props.pagination.page - 1}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={props.pagination.page}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElement
        num={props.pagination.page + 1}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
      <PaginationElipsis />
      <PaginationElement
        num={lastPage}
        setPagination={props.setPage}
        pagination={props.pagination}
      />
    </>
  );
};

const GenericPaginator = (inner: InnerPaginatorProps) => {
  if (inner.lastPage < 7) return FewPagesPaginator(inner);
  else if (
    inner.props.pagination.page < 2 ||
    inner.props.pagination.page > inner.lastPage - 2
  )
    return ExtremitiesPaginator(inner);
  else return MiddlePaginator(inner);
};

const onGoToPage = (
  pageInput: string,
  props: DatatablePaginationProps,
  lastPage: number,
) => {
  const parsedPage = parseInt(pageInput);

  if (isNaN(parsedPage) || parsedPage - 1 < 0 || parsedPage - 1 > lastPage)
    return;

  props.setPage({ page: parsedPage - 1, pageSize: props.pagination.pageSize });
};

export const DatatablePagination = (props: DatatablePaginationProps) => {
  const { t } = useTranslation();

  const [goToPageInput, setGoToPageInput] = useState("");

  const lastPage = useMemo(
    () =>
      props.totalSize <= 0
        ? 0
        : Math.floor((props.totalSize - 1) / props.pagination.pageSize),
    [props.totalSize, props.pagination.pageSize],
  );

  return (
    <div className="flex flex-row justify-between w-full">
      <Select
        value={props.pagination.pageSize.toString()}
        onValueChange={(v) =>
          props.setPage({
            page: 0,
            pageSize: parseInt(v),
          })
        }
      >
        <SelectTrigger className="w-20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="w-fit">
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="20">20</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>
      <nav className="flex flex-row gap-3 justify-center items-center">
        <Button
          type="button"
          className="text-gray-800 gap-1 flex flex-row p-0"
          variant={"link"}
          disabled={props.pagination.page == 0}
          onClick={() =>
            props.setPage({
              page: props.pagination.page - 1,
              pageSize: props.pagination.pageSize,
            })
          }
        >
          <span className="text-lg">&#8249;</span>
          {t("paginationPrecedent")}
        </Button>
        <div className="flex flex-row gap-0">
          <GenericPaginator lastPage={lastPage} props={props} />
        </div>
        <Button
          type="button"
          className="text-gray-800 gap-1 flex flex-row p-0"
          variant={"link"}
          disabled={props.pagination.page == lastPage}
          onClick={() =>
            props.setPage({
              page: props.pagination.page + 1,
              pageSize: props.pagination.pageSize,
            })
          }
        >
          {t("paginationSuivant")}
          <span className="text-lg">&#8250;</span>
        </Button>
      </nav>
      <div
        className="flex flex-row gap-3 items-center"
        onSubmit={(e) => {
          e.preventDefault();
          onGoToPage(goToPageInput, props, lastPage);
        }}
      >
        <Button
          type="button"
          className="text-gray-800"
          variant={"link"}
          onClick={() => {
            onGoToPage(goToPageInput, props, lastPage);
          }}
        >
          {t("paginationAllerALaPage")}
        </Button>
        <Input
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onGoToPage(goToPageInput, props, lastPage);
            }
          }}
          defaultValue={props.pagination.page + 1}
          className="w-20"
          type="number"
          style={{ appearance: "textfield" }}
          onChange={(v) => setGoToPageInput(v.target.value)}
        ></Input>
        {props.endContent ? (
          <div className="flex items-center gap-2 ml-1">{props.endContent}</div>
        ) : null}
      </div>
    </div>
  );
};
